import { sanitizeStyleId } from '../beerStyle/sanitizeStyleId';
import { toStyleNameKey } from '../beerStyle/styleNameKey';
import { beerNameIdentityKey } from './beerNameIdentityKey';
import {
  COMPARISON_FILTER_APPLY_ERRORS,
  COMPARISON_FILTER_NEEDS_REVIEW,
  candidateRequiresHumanReview,
  isChangedComparisonClassification,
} from './beerIngestionComparison';
import { isExtractionRun } from './beerIngestionRunFilters';

export const REVIEW_BULK_CLASSIFICATIONS = [
  'new',
  'changed_abv',
  'changed_style',
  'changed_description',
  'changed_multiple',
];

export const REVIEW_ACTION_LABELS = {
  approve: 'Approve',
  reject: 'Reject',
  acknowledge: 'Acknowledge',
};

export const REVIEW_DECISION_LABELS = {
  approve: 'Approved',
  reject: 'Rejected',
  acknowledge: 'Acknowledged',
};

export const APPLY_OUTCOME_LABELS = {
  added: 'Added',
  updated: 'Updated',
  duplicate: 'Duplicate',
  validation_error: 'Apply error',
  skipped: 'Skipped',
  pending: 'Apply pending',
};

const APPLY_ERROR_OUTCOME = 'validation_error';
const APPLY_STATUSES_WITH_ERRORS = new Set(['partial', 'failed']);
const APPLY_RETRY_STATUSES = new Set(['partial', 'failed', 'applying']);
const TERMINAL_REVIEW_APPLY_OUTCOMES = new Set(['added', 'updated', 'duplicate']);

export const DUPLICATE_APPROVED_NAME_CAPTION =
  'Same name as another approved beer in this review. Reject this row or edit the name.';

/**
 * Human label for a persisted apply outcome.
 *
 * @param {string|null|undefined} applyOutcome - Apply outcome
 * @returns {string|null}
 */
export function applyOutcomeLabel(applyOutcome) {
  if (!applyOutcome) {
    return null;
  }
  return APPLY_OUTCOME_LABELS[applyOutcome] ?? applyOutcome;
}

/**
 * Chip color for a persisted apply outcome.
 *
 * @param {string|null|undefined} applyOutcome - Apply outcome
 * @returns {'default'|'success'|'warning'|'error'}
 */
export function applyOutcomeColor(applyOutcome) {
  switch (applyOutcome) {
    case 'added':
    case 'updated':
      return 'success';
    case 'duplicate':
      return 'warning';
    case APPLY_ERROR_OUTCOME:
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Whether an apply outcome should appear in the Apply errors filter.
 *
 * @param {string|null|undefined} applyOutcome - Apply outcome
 * @returns {boolean}
 */
export function isApplyErrorOutcome(applyOutcome) {
  return applyOutcome === APPLY_ERROR_OUTCOME;
}

/**
 * Whether apply finished with leftover failures.
 *
 * @param {string|null|undefined} applyStatus - Review or run apply status
 * @returns {boolean}
 */
export function hasUnresolvedApplyErrors(applyStatus) {
  return APPLY_STATUSES_WITH_ERRORS.has(applyStatus);
}

/**
 * Whether Engage should show Retry apply for a leftover or failed apply.
 *
 * @param {string|null|undefined} applyStatus - Review or run apply status
 * @returns {boolean}
 */
export function isIngestionApplyRetryable(applyStatus) {
  return APPLY_RETRY_STATUSES.has(applyStatus);
}

/**
 * Default comparison filter for a review workspace.
 *
 * @param {object|null} review - Review document
 * @param {object|null} run - Run record
 * @returns {string}
 */
export function initialReviewCandidateFilter(review, run) {
  const applyStatus = review?.applyStatus ?? run?.applyStatus;
  if (hasUnresolvedApplyErrors(applyStatus)) {
    return COMPARISON_FILTER_APPLY_ERRORS;
  }
  return COMPARISON_FILTER_NEEDS_REVIEW;
}

const APPLY_LOCKED_STATUSES = new Set(['applying', 'applied']);

/**
 * Allowed review actions for a classification.
 *
 * @param {string} classification - Comparison classification
 * @returns {string[]}
 */
export function allowedReviewActions(classification) {
  switch (classification) {
    case 'new':
    case 'changed_abv':
    case 'changed_style':
    case 'changed_description':
    case 'changed_multiple':
    case 'invalid':
      return ['approve', 'reject'];
    default:
      return [];
  }
}

/**
 * Whether name / style / ABV can be edited inline for a classification.
 *
 * @param {string} classification - Comparison classification
 * @returns {boolean}
 */
export function isEditableReviewClassification(classification) {
  if (classification === 'new' || classification === 'invalid') {
    return true;
  }
  return isChangedComparisonClassification(classification);
}

/**
 * Draft fields copied from a comparison candidate.
 *
 * @param {object|null} candidate - Comparison candidate
 * @returns {{ name: string, style: string, abv: string, description: string }}
 */
export function proposedBeerDraftFromCandidate(candidate) {
  const source = candidate?.currentNormalized ?? candidate?.baselineNormalized;
  if (!source) {
    return { name: '', style: '', abv: '', description: '' };
  }
  return {
    name: source.name ?? '',
    style: source.style ?? '',
    abv: source.abv == null ? '' : String(source.abv),
    description: source.description ?? '',
  };
}

/**
 * Whether the operator changed name, style, or ABV from the extracted values.
 *
 * @param {object|null} draft - Current draft
 * @param {object|null} candidate - Comparison candidate
 * @returns {boolean}
 */
export function isReviewDraftEdited(draft, candidate) {
  if (!draft) {
    return false;
  }
  const original = proposedBeerDraftFromCandidate(candidate);
  return (draft.name ?? '') !== original.name
    || (draft.style ?? '') !== original.style
    || String(draft.abv ?? '') !== original.abv;
}

/**
 * Whether a persisted apply outcome freezes the row.
 *
 * @param {object|null} decision - Review decision
 * @returns {boolean}
 */
export function isReviewRowFrozen(decision) {
  return TERMINAL_REVIEW_APPLY_OUTCOMES.has(decision?.applyOutcome);
}

/**
 * Caption when this draft repeats another approved name that has not applied yet.
 *
 * @param {string} candidateKey - Candidate being approved
 * @param {string|null|undefined} draftName - Name that would be submitted
 * @param {object} decisions - Review decisions keyed by candidateKey
 * @returns {string|null}
 */
export function duplicateApprovedNameCaption(candidateKey, draftName, decisions) {
  const nameKey = beerNameIdentityKey(draftName);
  if (!nameKey) {
    return null;
  }
  const collides = Object.entries(decisions ?? {}).some(([decisionKey, decision]) => {
    if (decisionKey === candidateKey || decision?.action !== 'approve') {
      return false;
    }
    if (TERMINAL_REVIEW_APPLY_OUTCOMES.has(decision.applyOutcome)) {
      return false;
    }
    return beerNameIdentityKey(decision.proposedBeer?.name) === nameKey;
  });
  return collides ? DUPLICATE_APPROVED_NAME_CAPTION : null;
}

/**
 * Whether a candidate can be bulk-rejected (or selected for reject).
 *
 * @param {object|null} candidate - Comparison candidate
 * @param {object|null} decision - Existing decision
 * @returns {boolean}
 */
export function isRejectableReviewCandidate(candidate, decision) {
  if (!candidate) {
    return false;
  }
  if (isReviewRowFrozen(decision)) {
    return false;
  }
  return allowedReviewActions(candidate.classification).includes('reject');
}

/**
 * Resolves a proposed style label to a catalog id when possible.
 *
 * @param {string|null} styleLabel - Extracted or edited style label
 * @param {object} styleMapping - Catalog name/nameKey/id → beerStyles id
 * @param {Set<string>} beerStyleIds - beerStyles document ids
 * @returns {{ style: string|null, isCatalogStyle: boolean, extractedLabel: string|null }}
 */
export function resolveProposedStyleForReview(
  styleLabel,
  styleMapping = {},
  beerStyleIds = new Set(),
) {
  const trimmed = typeof styleLabel === 'string' ? styleLabel.trim() : '';
  if (!trimmed) {
    return { style: null, isCatalogStyle: false, extractedLabel: null };
  }
  const mapped = styleMapping[trimmed] || styleMapping[toStyleNameKey(trimmed)];
  if (mapped && beerStyleIds.has(mapped)) {
    return { style: mapped, isCatalogStyle: true, extractedLabel: trimmed };
  }
  if (beerStyleIds.has(trimmed)) {
    return { style: trimmed, isCatalogStyle: true, extractedLabel: trimmed };
  }
  const sanitized = sanitizeStyleId(trimmed);
  if (sanitized && beerStyleIds.has(sanitized)) {
    return { style: sanitized, isCatalogStyle: true, extractedLabel: trimmed };
  }
  return { style: trimmed, isCatalogStyle: false, extractedLabel: trimmed };
}

/**
 * Resolves styleType for a non-catalog proposed style.
 *
 * @param {string|null} styleLabel - Extracted style label
 * @param {boolean} isCatalogStyle - Whether the style resolved to catalog
 * @param {object} sessionStyleTypes - Session-assigned style types
 * @param {object} [styleMapping] - Unused; kept for call-site compatibility
 * @returns {string|null}
 */
export function resolveProposedStyleType(
  styleLabel,
  isCatalogStyle,
  sessionStyleTypes = {},
) {
  if (isCatalogStyle || !styleLabel) {
    return null;
  }
  return sessionStyleTypes[styleLabel] ?? null;
}

/**
 * Caption for a mapped style type, or null when the operator still needs to assign one.
 *
 * @param {string|null} styleLabel - Extracted or edited style
 * @param {object} styleMapping - Catalog name/nameKey/id → beerStyles id
 * @param {Array<object>} beerStyles - Catalog style rows
 * @param {object} sessionStyleTypes - Session-assigned style types
 * @returns {string|null}
 */
export function styleTypeCaptionForLabel(
  styleLabel,
  styleMapping = {},
  beerStyles = [],
  sessionStyleTypes = {},
) {
  const trimmed = typeof styleLabel === 'string' ? styleLabel.trim() : '';
  if (!trimmed) {
    return null;
  }
  const sessionType = sessionStyleTypes[trimmed];
  if (typeof sessionType === 'string' && sessionType.trim()) {
    return sessionType.trim();
  }
  const beerStyleIds = new Set(
    (Array.isArray(beerStyles) ? beerStyles : [])
      .map((styleRow) => styleRow?.id)
      .filter(Boolean),
  );
  const resolved = resolveProposedStyleForReview(trimmed, styleMapping, beerStyleIds);
  if (!resolved.isCatalogStyle) {
    return null;
  }
  const catalogStyle = (Array.isArray(beerStyles) ? beerStyles : []).find((styleRow) => (
    styleRow?.id === resolved.style || styleRow?.name === trimmed
  ));
  const typeName = typeof catalogStyle?.type === 'string' ? catalogStyle.type.trim() : '';
  return typeName || null;
}

/**
 * Returns whether an extracted style already maps to a catalog style or type.
 *
 * @param {string|null} styleName - Extracted style
 * @param {object} styleMapping - Catalog name/nameKey/id → beerStyles id
 * @param {Set<string>} beerStyleIds - beerStyles document ids
 * @param {object} sessionStyleTypes - Session-assigned style types
 * @returns {boolean}
 */
export function isExtractedStyleMapped(
  styleName,
  styleMapping = {},
  beerStyleIds = new Set(),
  sessionStyleTypes = {},
) {
  const trimmed = typeof styleName === 'string' ? styleName.trim() : '';
  if (!trimmed) {
    return false;
  }
  if (
    styleMapping[trimmed]
    || styleMapping[toStyleNameKey(trimmed)]
    || sessionStyleTypes[trimmed]
  ) {
    return true;
  }
  const sanitized = sanitizeStyleId(trimmed);
  return Boolean(sanitized && beerStyleIds.has(sanitized)) || beerStyleIds.has(trimmed);
}

/**
 * Unique extracted styles that still need a style type.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {object} styleMapping - Existing mappings
 * @param {Set<string>} beerStyleIds - Catalog style ids
 * @param {object} sessionStyleTypes - Session types
 * @returns {string[]}
 */
export function unknownExtractedStyles(
  candidates,
  styleMapping,
  beerStyleIds,
  sessionStyleTypes,
) {
  const seen = new Set();
  const unknown = [];
  (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
    if (!candidateRequiresHumanReview(candidate)) {
      return;
    }
    const styleName = candidate.currentNormalized?.style;
    const trimmed = typeof styleName === 'string' ? styleName.trim() : '';
    if (!trimmed || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    if (!isExtractedStyleMapped(trimmed, styleMapping, beerStyleIds, sessionStyleTypes)) {
      unknown.push(trimmed);
    }
  });
  return unknown;
}

const RESOLVED_APPLY_OUTCOMES = new Set(['added', 'updated', 'duplicate']);

/**
 * Style label used when applying an approved decision.
 *
 * @param {object} decision - Review decision
 * @param {object|null} candidate - Matching comparison candidate
 * @returns {string}
 */
export function approvedDecisionStyleLabel(decision, candidate) {
  const proposedStyle = typeof decision?.proposedBeer?.style === 'string'
    ? decision.proposedBeer.style.trim()
    : '';
  if (proposedStyle) {
    return proposedStyle;
  }
  const extractedStyle = typeof candidate?.currentNormalized?.style === 'string'
    ? candidate.currentNormalized.style.trim()
    : '';
  return extractedStyle;
}

/**
 * Unique unmapped styles on approved beers that still need apply.
 *
 * Rejected beers and already-published apply outcomes are ignored.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {object} decisions - Review decisions keyed by candidateKey
 * @param {object} styleMapping - Existing mappings
 * @param {Set<string>} beerStyleIds - Catalog style ids
 * @param {object} sessionStyleTypes - Session types
 * @returns {string[]}
 */
export function unmappedApprovedStyleNames(
  candidates,
  decisions,
  styleMapping,
  beerStyleIds,
  sessionStyleTypes,
) {
  const candidateByKey = new Map(
    (Array.isArray(candidates) ? candidates : []).map((candidate) => [
      candidate.candidateKey,
      candidate,
    ]),
  );
  const seen = new Set();
  const unknown = [];
  Object.entries(decisions && typeof decisions === 'object' ? decisions : {}).forEach(
    ([decisionKey, decision]) => {
      if (decision?.action !== 'approve') {
        return;
      }
      if (RESOLVED_APPLY_OUTCOMES.has(decision.applyOutcome)) {
        return;
      }
      const proposedType = typeof decision.proposedBeer?.styleType === 'string'
        ? decision.proposedBeer.styleType.trim()
        : '';
      if (proposedType) {
        return;
      }
      const lookupKey = decision.candidateKey || decisionKey;
      const styleName = approvedDecisionStyleLabel(
        decision,
        candidateByKey.get(lookupKey),
      );
      if (!styleName || seen.has(styleName)) {
        return;
      }
      seen.add(styleName);
      if (!isExtractedStyleMapped(styleName, styleMapping, beerStyleIds, sessionStyleTypes)) {
        unknown.push(styleName);
      }
    },
  );
  return unknown;
}

/**
 * Operator copy when approved beers still need style types.
 *
 * @param {string[]} styleNames - Unmapped approved style labels
 * @param {'complete'|'apply'} phase - Review or apply gate
 * @returns {string|null}
 */
export function unmappedApprovedStylesCopy(styleNames, phase) {
  const names = Array.isArray(styleNames) ? styleNames.filter((name) => name) : [];
  if (names.length === 0) {
    return null;
  }
  const listed = names.join(', ');
  if (phase === 'apply') {
    return `Assign a style type for ${listed} before applying approved beers. Rejected beers do not need a mapping.`;
  }
  return `Assign a style type for ${listed}, or reject those beers, before completing review.`;
}

/**
 * Snapshot beer matching a candidate's current index.
 *
 * @param {object} candidate - Comparison candidate
 * @param {Array<object>} snapshotBeers - Snapshot rows
 * @returns {object|null}
 */
export function snapshotBeerForCandidate(candidate, snapshotBeers) {
  if (candidate?.currentBeerIndex == null || !Array.isArray(snapshotBeers)) {
    return null;
  }
  return snapshotBeers.find((beer) => beer.index === candidate.currentBeerIndex) ?? null;
}

/**
 * Snapshot row used to render comparison-table beer fields.
 *
 * @param {object} candidate - Comparison candidate
 * @param {Array<object>} snapshotBeers - Snapshot rows
 * @returns {object|null}
 */
export function snapshotBeerForComparisonRow(candidate, snapshotBeers) {
  const snapshotBeer = snapshotBeerForCandidate(candidate, snapshotBeers);
  if (snapshotBeer) {
    return snapshotBeer;
  }
  const normalized = candidate?.currentNormalized ?? candidate?.baselineNormalized;
  if (!normalized) {
    return null;
  }
  return {
    normalized,
    raw: {},
    issues: [],
  };
}

/**
 * Queue counts for required human-review rows.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {object} decisions - Review decisions keyed by candidateKey
 * @returns {{ pendingCount: number, decidedCount: number }}
 */
export function countReviewQueueFromCandidates(candidates, decisions = {}) {
  const required = (Array.isArray(candidates) ? candidates : [])
    .filter((candidate) => candidateRequiresHumanReview(candidate));
  const decidedCount = required.filter((candidate) => (
    Boolean(decisions[candidate.candidateKey])
  )).length;
  return {
    pendingCount: Math.max(required.length - decidedCount, 0),
    decidedCount,
  };
}

/**
 * Whether a candidate can be bulk-approved.
 *
 * @param {object} candidate - Comparison candidate
 * @param {object|null} snapshotBeer - Matching snapshot row
 * @param {boolean} styleMapped - Style already mapped
 * @returns {boolean}
 */
export function isBulkEligibleReviewCandidate(candidate, snapshotBeer, styleMapped) {
  if (!candidateRequiresHumanReview(candidate)) {
    return false;
  }
  if (!REVIEW_BULK_CLASSIFICATIONS.includes(candidate.classification)) {
    return false;
  }
  if (candidate.evidence?.duplicateNameKey === true) {
    return false;
  }
  if (!snapshotBeer || snapshotBeer.canApprove !== true) {
    return false;
  }
  return styleMapped === true;
}

/**
 * Human copy for missing (off-menu) observations.
 *
 * @returns {string}
 */
export function missingCandidateCopy() {
  return 'Not on the current menu/source. The catalog keeps beers the brewery has brewed, so this will not be deleted. No action is required.';
}

/**
 * Summary chips for new/changed counts.
 *
 * @param {number} decidedCount - Decided required rows
 * @param {number} pendingCount - Remaining required rows
 * @returns {string}
 */
export function reviewProgressLabel(decidedCount, pendingCount) {
  const total = (Number(decidedCount) || 0) + (Number(pendingCount) || 0);
  return `${Number(decidedCount) || 0} of ${total} decided`;
}

/**
 * Progress label for apply-phase inbox rows.
 *
 * @param {string|null} applyStatus - Run apply status
 * @returns {string}
 */
export function inboxApplyProgressLabel(applyStatus) {
  switch (applyStatus) {
    case 'failed':
      return 'Apply failed';
    case 'partial':
      return 'Partially applied';
    case 'applying':
      return 'Applying…';
    default:
      return 'Ready to apply';
  }
}

/**
 * Progress column text for a review inbox row.
 *
 * @param {object} row - Queue row
 * @returns {string}
 */
export function inboxProgressLabel(row = {}) {
  if (row.queuePhase === 'apply') {
    return inboxApplyProgressLabel(row.applyStatus);
  }
  return reviewProgressLabel(row.reviewDecidedCount, row.reviewPendingCount);
}

/**
 * Primary inbox action label for a queue row.
 *
 * @param {object} row - Queue row
 * @returns {string}
 */
export function inboxActionLabel(row = {}) {
  return row.queuePhase === 'apply' ? 'Apply' : 'Review';
}

/**
 * Action label for a brewery ingestion-runs table row.
 *
 * @param {object} run - Run list record
 * @returns {string}
 */
export function breweryRunListActionLabel(run = {}) {
  if (run.hasComparison) {
    return inboxActionLabel(run);
  }
  return 'View';
}

/**
 * Summary chips for new/changed/rename counts.
 *
 * @param {object} source - Run or queue row
 * @returns {string}
 */
export function reviewChangeSummaryLabel(source = {}) {
  const newCount = Number(source.candidatesNewCount) || 0;
  const changedCount = Number(source.candidatesChangedCount) || 0;
  return `${newCount} new · ${changedCount} changed`;
}

/**
 * Changes summary for a brewery ingestion-runs table row.
 *
 * @param {object} run - Run list record
 * @returns {string}
 */
export function breweryRunChangesLabel(run = {}) {
  if (isExtractionRun(run)) {
    if (run.hasComparison) {
      return reviewChangeSummaryLabel(run);
    }
    return '—';
  }
  const addedCount = Number(run.beersAddedCount) || 0;
  const updatedCount = Number(run.beersUpdatedCount) || 0;
  return `${addedCount} added · ${updatedCount} updated`;
}

function countOrListLength(value, length) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : length;
}

function stringNameList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item) => typeof item === 'string' && item.trim());
}

function buildStoredImportStatus(addedCount, updatedCount, errorCount) {
  const parts = [];
  if (addedCount > 0) {
    parts.push(addedCount === 1 ? '1 beer added' : `${addedCount} beers added`);
  }
  if (updatedCount > 0) {
    parts.push(updatedCount === 1 ? '1 beer updated' : `${updatedCount} beers updated`);
  }
  if (errorCount > 0) {
    parts.push(errorCount === 1 ? '1 error encountered' : `${errorCount} errors encountered`);
  }
  return parts.join(', ');
}

/**
 * Maps a persisted import ingestion run onto the ImportSummaryView shape.
 * Duplicate rows and beer IDs are not stored on the run, so those stay empty.
 *
 * @param {object|null} run - Ingestion run record
 * @returns {object|null}
 */
export function importResultsFromIngestionRun(run) {
  if (!run || isExtractionRun(run)) {
    return null;
  }
  const addedNames = stringNameList(run.beersAddedList);
  const updatedNames = stringNameList(run.beersUpdatedList);
  const erroredNames = stringNameList(run.beersErroredList);
  const addedCount = countOrListLength(run.beersAddedCount, addedNames.length);
  const updatedCount = countOrListLength(run.beersUpdatedCount, updatedNames.length);
  const errorCount = countOrListLength(run.beersErroredCount, erroredNames.length);

  return {
    addedCount,
    updatedCount,
    duplicatedCount: 0,
    errorCount,
    addedBeers: addedNames.map((name) => ({ id: '', name })),
    updatedBeers: updatedNames.map((name) => ({
      inputBeer: { name },
      updatedBeer: { name },
    })),
    duplicatedBeers: [],
    errors: erroredNames.map((name) => ({ id: '', name, message: '' })),
    status: buildStoredImportStatus(addedCount, updatedCount, errorCount),
  };
}

/**
 * Whether the review is locked for further decisions.
 *
 * Writable until apply has started or finished. Completed but unapplied
 * reviews stay editable (ITT-924).
 *
 * @param {object|null} review - Review document
 * @param {object|null} run - Run record
 * @returns {boolean}
 */
export function isIngestionReviewLocked(review, run) {
  const applyStatus = review?.applyStatus ?? run?.applyStatus;
  return APPLY_LOCKED_STATUSES.has(applyStatus);
}

/**
 * Whether approved changes were already applied to the catalog.
 *
 * @param {object|null} review - Review document
 * @returns {boolean}
 */
export function isIngestionReviewApplied(review) {
  return review?.applyStatus === 'applied';
}

/**
 * Filters visible candidates by classification or apply-error filter.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {string} filter - Active filter
 * @param {object} decisions - Review decisions keyed by candidateKey
 * @returns {Array<object>}
 */
export function filterReviewCandidates(candidates, filter, decisions = {}) {
  const rows = Array.isArray(candidates) ? candidates : [];
  return rows.filter((candidate) => {
    if (!filter || filter === 'all') {
      return true;
    }
    if (filter === COMPARISON_FILTER_NEEDS_REVIEW) {
      return candidateRequiresHumanReview(candidate)
        || candidate.classification === 'missing';
    }
    if (filter === 'changed') {
      return isChangedComparisonClassification(candidate.classification);
    }
    if (filter === COMPARISON_FILTER_APPLY_ERRORS) {
      return isApplyErrorOutcome(decisions[candidate.candidateKey]?.applyOutcome);
    }
    return candidate.classification === filter;
  });
}

/**
 * Mapped extracted style names for the bulk allowlist.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {object} styleMapping - Existing mappings
 * @param {Set<string>} beerStyleIds - Catalog ids
 * @param {object} sessionStyleTypes - Session types
 * @returns {string[]}
 */
export function mappedStyleNamesForBulk(
  candidates,
  styleMapping,
  beerStyleIds,
  sessionStyleTypes,
) {
  const names = new Set();
  (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
    const styleName = typeof candidate?.currentNormalized?.style === 'string'
      ? candidate.currentNormalized.style.trim()
      : '';
    if (
      styleName
      && isExtractedStyleMapped(styleName, styleMapping, beerStyleIds, sessionStyleTypes)
    ) {
      names.add(styleName);
    }
  });
  return Array.from(names);
}
